import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../authConfig';
import axios from 'axios';
import { PointConversionResponse } from '../Types/entities';
import { safeAcquireTokenSilent } from './msalHelpers';

const API_URL = process.env.REACT_APP_API_URL;

const fetchToken = async (instance: any, accounts: any[]) => {
    // Use the safe helper to acquire token
    const res = await safeAcquireTokenSilent(instance, accounts, loginRequest.scopes);
    return res.idToken;
};

export const handleApiError = (error: unknown, fallbackData?: any) => {
    console.error("API Error:", error);
    if (fallbackData) {
        return fallbackData;
    }
    throw error;
};

export const fetchPointConversionData = async (instance: any, accounts: any[]) => {
    try {
        const token = await fetchToken(instance, accounts);
        const apiResponse = await axios.get(`${API_URL}/api/PointConversion/get-all-data`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return { apiResponse: apiResponse };
    }
    catch (error) {
        console.error("Error details:", error);
        return handleApiError(error, null);
    }
};

export const fetchCountryDetailsData = async (instance: any, accounts: any[]) => {
    try {
        const token = await fetchToken(instance, accounts);
        const apiResponse = await axios.get(`${API_URL}/api/PointConversion/get-all-country-data`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return { apiResponse: apiResponse };
    }
    catch (error) {
        console.error("Error details:", error);
        return handleApiError(error, null);
    }
};

export const savePointConversionData = async (instance: any, accounts: any[], data: PointConversionResponse) => {
    try {
        const token = await fetchToken(instance, accounts);
        const apiResponse = await axios.post(`${API_URL}/api/PointConversion/save-point-conversion`, data,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                },
            }
        );
        return { apiResponse: apiResponse };
    }
    catch (error) {
        console.error("Error details:", error);
        return handleApiError(error, null);
    }
};

export const updatePointConversionData = async (instance: any, accounts: any[], data: PointConversionResponse) => {
    try {
        const token = await fetchToken(instance, accounts);
        const apiResponse = await axios.patch(`${API_URL}/api/PointConversion/update-point-conversion`, data,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                },
            }
        );
        return { apiResponse: apiResponse };
    }
    catch (error) {
        console.error("Error details:", error);
        return handleApiError(error, null);
    }
};

export const fetchRejectedGEMAwards = async (instance: any, accounts: any[]) => {
    try {
        const token = await fetchToken(instance, accounts);
        const apiResponse = await axios.get(`${API_URL}/api/admin/getRejectedGEMAwards`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return { apiResponse: apiResponse };
    }
    catch (error) {
        console.error("Error details:", error);
        return handleApiError(error, null);
    }
};

export const fetchApprovedGEMAwards = async (instance: any, accounts: any[]) => {
    try {
        const token = await fetchToken(instance, accounts);
        const apiResponse = await axios.get(`${API_URL}/api/admin/getApprovedGEMAwards`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return { apiResponse: apiResponse };
    }
    catch (error) {
        console.error("Error details:", error);
        return handleApiError(error, null);
    }
};

export const fetchPointAllocatedByAwards = async (instance: any, accounts: any[], startDate: Date, endDate: Date) => {
    try {
        const token = await fetchToken(instance, accounts);
        
        // Format dates as YYYY-MM-DD to avoid timezone conversion issues
        const formatDate = (date: Date): string => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };
        
        const startDateStr = formatDate(startDate);
        const endDateStr = formatDate(endDate);
        
        const apiResponse = await axios.get(`${API_URL}/api/admin/getPointsAllocatedByAwards/${startDateStr}/${endDateStr}`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return { apiResponse: apiResponse };
    }
    catch (error) {
        console.error("Error details:", error);
        return handleApiError(error, null);
    }
};

/** Dimension values that populate the Insights dashboard filter row. */
export const fetchInsightsFilterOptions = async (instance: any, accounts: any[]) => {
    try {
        const token = await fetchToken(instance, accounts);
        const apiResponse = await axios.get(`${API_URL}/api/admin/insights/filters`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return { apiResponse: apiResponse };
    }
    catch (error) {
        console.error("Error details:", error);
        return handleApiError(error, null);
    }
};

export interface InsightsDashboardFilters {
    fromDate: string;      // YYYY-MM-DD
    toDate: string;        // YYYY-MM-DD
    countries?: string[];
    locations?: string[];
    managerIds?: string[];
    awardTypes?: string[];
    departments?: string[];
    businessUnits?: string[];
    unitHeadIds?: string[];
    /** Reward type ids from the grouped Award type dropdown (names repeat across parents). */
    rewardTypeIds?: string[];
    /** Ask the dashboard to also return the filter dimension lists, saving a round trip. */
    includeFilters?: boolean;
}

/**
 * Single aggregated payload behind the Insights dashboard. Every tile and chart on the
 * page reads from this one response, so the numbers always agree with each other.
 */
export const fetchInsightsDashboard = async (
    instance: any,
    accounts: any[],
    filters: InsightsDashboardFilters
) => {
    try {
        const token = await fetchToken(instance, accounts);
        const apiResponse = await axios.get(`${API_URL}/api/admin/insights/dashboard`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: {
                    fromDate: filters.fromDate,
                    toDate: filters.toDate,
                    countries: filters.countries?.length ? filters.countries : undefined,
                    locations: filters.locations?.length ? filters.locations : undefined,
                    managerIds: filters.managerIds?.length ? filters.managerIds : undefined,
                    awardTypes: filters.awardTypes?.length ? filters.awardTypes : undefined,
                    departments: filters.departments?.length ? filters.departments : undefined,
                    businessUnits: filters.businessUnits?.length ? filters.businessUnits : undefined,
                    unitHeadIds: filters.unitHeadIds?.length ? filters.unitHeadIds : undefined,
                    rewardTypeIds: filters.rewardTypeIds?.length ? filters.rewardTypeIds : undefined,
                    includeFilters: filters.includeFilters ? true : undefined,
                },
                // ASP.NET binds string[]/Guid[] from repeated keys (?countries=A&countries=B).
                paramsSerializer: {
                    indexes: null,
                },
            }
        );
        return { apiResponse: apiResponse };
    }
    catch (error) {
        console.error("Error details:", error);
        return handleApiError(error, null);
    }
};

export const fetchMainBudgetData = async (instance: any, accounts: any[]) => {
    try {
        const token = await fetchToken(instance, accounts);
        const apiResponse = await axios.get(`${API_URL}/api/admin/getMainBudgetData`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return { apiResponse: apiResponse };
    }
    catch (error) {
        console.error("Error details:", error);
        return handleApiError(error, null);
    }
};